/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Modal, Steps, Input, Button, Alert, message, Icon } from 'antd';
import QRCode from 'react-qr-code';
import { MFA_COPY, getAuthenticatorAppLabel } from '../../../../../constants/mfa';
import { beginAuthenticatorEnrollment, verifyAuthenticatorEnrollment } from '../UserActions';

import './AuthenticatorEnrollmentModal.scss';

const { Step } = Steps;

function copyToClipboard(text) {
  if (!text) return;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(
      () => message.success('Copied'),
      () => message.error('Could not copy'),
    );
  }
}

const AuthenticatorEnrollmentModal = ({
  visible,
  otpType,
  onCancel,
  onCompleted,
  beginAuthenticatorEnrollment: beginEnrollment,
  verifyAuthenticatorEnrollment: verifyEnrollment,
}) => {
  const appLabel = getAuthenticatorAppLabel(otpType);
  const [step, setStep] = useState(0);
  const [loadingBegin, setLoadingBegin] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [showManualSecret, setShowManualSecret] = useState(false);

  const reset = useCallback(() => {
    setStep(0);
    setEnrollment(null);
    setCode('');
    setShowManualSecret(false);
  }, []);

  useEffect(() => {
    if (!visible) reset();
  }, [visible, reset]);

  useEffect(() => {
    if (!visible) return undefined;

    let cancelled = false;
    (async () => {
      setLoadingBegin(true);
      try {
        const data = await beginEnrollment();
        if (!cancelled) {
          setEnrollment(data);
          setStep(0);
        }
      } catch (err) {
        message.error(typeof err === 'string' ? err : 'Could not start authenticator setup');
        if (!cancelled) onCancel();
      } finally {
        if (!cancelled) setLoadingBegin(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, beginEnrollment, onCancel]);

  const onVerify = async () => {
    const trimmed = `${code}`.replace(/\s/g, '');
    if (trimmed.length < 6) {
      message.warning(`Enter the 6-digit code from ${appLabel}`);
      return;
    }
    setLoadingVerify(true);
    try {
      await verifyEnrollment({ code: trimmed, otpType });
      message.success(`${appLabel} is ready to use`);
      onCompleted();
      onCancel();
    } catch {
      message.error(MFA_COPY.enrollmentVerifyFailed);
    } finally {
      setLoadingVerify(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          <Icon type="safety-certificate" style={{ marginRight: 8, color: '#343579' }} />
          {appLabel}
        </span>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="min(520px, 92vw)"
      destroyOnClose
      className="authenticator-enrollment-modal"
    >
      <p className="authenticator-modal-lead">
        {`Link your ContinuityCoach account in ${appLabel}, then use the rotating code when you sign in.`}
      </p>

      <Steps current={step} className="authenticator-steps">
        <Step title="Scan QR code" description="In the app" />
        <Step title="Verify" description="Enter code" />
      </Steps>

      {loadingBegin && (
        <Alert type="info" showIcon message="Preparing secure enrollment…" style={{ marginBottom: 16 }} />
      )}

      {!loadingBegin && enrollment && step === 0 && (
        <div className="authenticator-enrollment-step">
          <p>
            Open <strong>{appLabel}</strong>, add an account, then scan the QR code below.
          </p>

          {enrollment.otpauthUri ? (
            <div className="authenticator-qr-wrapper">
              <QRCode value={enrollment.otpauthUri} size={180} className="authenticator-qr-code" />
            </div>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="QR code unavailable"
              description="The server did not return an otpauth URI. Use the secret key below to add the account manually."
              style={{ marginBottom: 12 }}
            />
          )}

          <p className="authenticator-enrollment-tip" role="note">
            <span className="authenticator-enrollment-tip-icon" aria-hidden>
              💡
            </span>{' '}
            <strong>Tip:</strong> If you already have an entry for ContinuityCoach in your app, please delete it first
            before scanning this new code to avoid confusion.
          </p>

          <button type="button" className="authenticator-manual-toggle" onClick={() => setShowManualSecret((s) => !s)}>
            <Icon type={showManualSecret ? 'up' : 'down'} style={{ marginRight: 5, fontSize: 11 }} />
            {showManualSecret ? 'Hide secret key' : "Can't scan? Enter secret key manually"}
          </button>

          {showManualSecret && (
            <>
              {(enrollment.issuer || enrollment.accountName) && (
                <div className="authenticator-meta">
                  <span>
                    <strong>Issuer</strong> {enrollment.issuer || '—'}
                  </span>
                  <span>
                    <strong>Account</strong> {enrollment.accountName || '—'}
                  </span>
                </div>
              )}
              {enrollment.sharedSecret ? (
                <div className="authenticator-secret-block">
                  <div className="authenticator-secret-label">Secret key</div>
                  <div className="authenticator-secret-row">
                    <code className="authenticator-secret">{enrollment.sharedSecret}</code>
                    <Button
                      type="default"
                      size="small"
                      className="authenticator-copy-btn"
                      onClick={() => copyToClipboard(enrollment.sharedSecret)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="No secret key returned from the server"
                  style={{ marginBottom: 12 }}
                />
              )}
            </>
          )}

          <div className="authenticator-continue-wrap">
            <Button type="primary" className="authenticator-continue-btn" onClick={() => setStep(1)}>
              Continue to verification
            </Button>
          </div>
        </div>
      )}

      {!loadingBegin && enrollment && step === 1 && (
        <div className="authenticator-enrollment-step">
          <p className="authenticator-verify-lead">
            Enter the <strong>6-digit code</strong> currently shown in your authenticator app for this account.
          </p>
          <Input
            className="authenticator-code-input"
            placeholder="000000"
            maxLength={8}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
            autoComplete="one-time-code"
            inputMode="numeric"
            size="large"
            onPressEnter={onVerify}
          />
          <div className="authenticator-enrollment-actions">
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button type="primary" loading={loadingVerify} onClick={onVerify}>
              Verify &amp; activate
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const mapDispatchToProps = {
  beginAuthenticatorEnrollment,
  verifyAuthenticatorEnrollment,
};

export default connect(null, mapDispatchToProps)(AuthenticatorEnrollmentModal);
